import type { AnalyticsBrowser, EventProperties, UserTraits } from '@segment/analytics-next';

import type {
  BeaconEventOptions,
  SegmentClientName,
  SegmentManagerInterface,
  SegmentNamespace
} from '@/typings/segment-manager';

import { bootstrapExperimentCookies } from '@/utils/ab-testing';
import { SegmentManager } from './segment-manager';
import { SegmentConsentCoordinator } from './segment-consent';
import { sharedSegmentIdentity } from './segment-identity';
import { logToConsole } from '@/utils/logging';

class SegmentRegistry implements SegmentNamespace {
  private readonly clients = new Map<SegmentClientName, SegmentManager>();
  private readonly consent = new SegmentConsentCoordinator();

  readonly booking: SegmentManager;

  constructor() {
    this.booking = this.createClient('booking');
  }

  getOrCreateWebsite(): SegmentManager {
    return this.createClient('website');
  }

  get website(): SegmentManager {
    return this.getOrCreateWebsite();
  }

  private createClient(name: SegmentClientName): SegmentManager {
    const existing = this.clients.get(name);

    if (existing !== undefined) {
      return existing;
    }

    if (name === 'booking') {
      bootstrapExperimentCookies();
    }

    const manager = new SegmentManager({
      name,
      consent: this.consent,
      resetAll: this.reset
    });

    this.clients.set(name, manager);
    void manager.initializeWithConsentListening();

    return manager;
  }

  // These methods preserve the original booking bridge during the CMS switchover.
  async track(eventName: string, properties?: EventProperties, options?: BeaconEventOptions): Promise<void> {
    return this.booking.track(eventName, properties, options);
  }

  async page(pageName: string, properties?: EventProperties, options?: BeaconEventOptions): Promise<void> {
    return this.booking.page(pageName, properties, options);
  }

  async identify(userId: string | null, traits?: UserTraits, options?: BeaconEventOptions): Promise<void> {
    return this.booking.identify(userId, traits, options);
  }

  async group(groupId: string, traits?: UserTraits, options?: BeaconEventOptions): Promise<void> {
    return this.booking.group(groupId, traits, options);
  }

  async initializeAbTesting(): Promise<void> {
    return this.booking.initializeAbTesting();
  }

  async initializeWithConsentListening(): Promise<boolean> {
    return this.booking.initializeWithConsentListening();
  }

  async getAnonymousId(): Promise<string | null> {
    return this.booking.getAnonymousId();
  }

  getAnalyticsInstance(): AnalyticsBrowser {
    return this.booking.getAnalyticsInstance();
  }

  readonly reset = (): void => {
    window.segmentEventQueue?.splice(0);
    window.segmentWebsiteEventQueue?.splice(0);

    for (const client of this.clients.values()) {
      client.clearEventQueue();
      client.resetUnregisteredClient();
    }

    sharedSegmentIdentity.reset();
  };
}

function isNamespace(value: unknown): value is SegmentNamespace {
  return typeof value === 'object' && value !== null &&
    'booking' in value && 'website' in value &&
    'reset' in value && typeof value.reset === 'function';
}

export function initializeSegmentClients(): SegmentNamespace {
  if (isNamespace(window.segmentManager)) {
    return window.segmentManager;
  }

  if (window.segmentManager !== undefined) {
    throw new Error('A different Segment manager is already running on this page');
  }

  const namespace = new SegmentRegistry();

  window.segmentManager = namespace;

  if ((window.segmentWebsiteEventQueue?.length ?? 0) > 0) {
    // No later call is needed to start delivery of early CMS/widget events.
    namespace.getOrCreateWebsite();
  }

  return namespace;
}

/** Existing callers still await the booking client and its initial queue drain. */
export async function initSegmentManager(): Promise<SegmentManagerInterface> {
  const manager = initializeSegmentClients().booking;
  const allowed = await manager.initializeWithConsentListening();

  if (allowed && 'processEventQueue' in manager && typeof manager.processEventQueue === 'function') {
    try {
      await manager.processEventQueue();
    } catch (error: unknown) {
      logToConsole('Failed to process Segment event queue:', error);
    }
  }

  return manager;
}
